import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { type ProviderApi } from '@/features/iam/api/iam-api'
import { Provider } from './Provider'
import { mapProviderResourceToProps } from './provider-mappers'
import type { PrimeProviderProps } from '@/features/iam'

/**
 * The provider details a user can edit.
 */
export type UpdateProviderParams = Pick<
  Provider,
  | 'softwareOwned'
  | 'softwareExperience'
  | 'supplementalSoftwareExperience'
  | 'specialties'
  | 'stateRegion'
  | 'yearsExperience'
>

export class ProviderStore {
  /**
   * Provider collection.
   */
  readonly providers = collection({
    getDataId: (data) => data.userPublicId,
    getModelId: (model) => model.userPublicId,
    create: () => new Provider(),
  })

  constructor(private readonly _api: ProviderApi) {}

  /**
   * Fetches provider data if it is not already in the store.
   * Returns immediately.
   */
  primeProvider(props: PrimeProviderProps): Provider | undefined {
    if (!props.userPublicId) {
      return
    }

    const provider = this.providers.set({
      userPublicId: props.userPublicId,
    })

    void this.fetchByPublicId(props.userPublicId)

    return provider
  }

  /**
   * Fetches provider data for a user.
   * @param publicId
   * @param signal
   */
  async fetchByPublicId(publicId: Id, signal?: AbortSignal): Promise<void> {
    const provider = await this._api.get(Id.assert(publicId), signal)

    this.providers.set(mapProviderResourceToProps(provider))
  }

  /**
   * Updates a provider's editable user data, then refreshes the cached
   * Provider so observers re-render with the persisted values.
   * @param publicId
   * @param params
   */
  async updateProvider(
    publicId: Id,
    params: UpdateProviderParams,
  ): Promise<void> {
    await this._api.updateUserData(Id.assert(publicId), {
      software_owned: params.softwareOwned,
      software_experience: params.softwareExperience,
      supplemental_software_experience: params.supplementalSoftwareExperience,
      pro_specialties: params.specialties,
      state_region: params.stateRegion,
      years_experience: params.yearsExperience,
    })
    await this.fetchByPublicId(publicId)
  }
}
